import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { I } from '../icons-ds';
import { Tag, Kbd, Highlight } from '../ui';
import { usePhrases } from '../lib/PhrasesContext';
import { hydrateSearchResults, type SearchSnippet } from '../lib/search';
import { usePhraseSearch } from '../hooks/usePhraseSearch';
import type { Phrase } from '../lib/types';

export type CommandPaletteProps = {
  open: boolean;
  onClose: () => void;
};

type Item = { phrase: Phrase; snippet?: SearchSnippet; terms: string[] };

const MAX_ITEMS = 8;

/** Global ⌘K / Ctrl-K quick-open: ranked search from anywhere, keyboard-driven. */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { phrases, search, recentIds } = usePhrases();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const byId = useMemo(() => new Map(phrases.map((p) => [p.id, p])), [phrases]);
  const { results: rankedResults, searching } = usePhraseSearch(query, search, 25);

  // Reset and focus each time the palette opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(0);
    const t = setTimeout(() => inputRef.current?.focus(), 20);
    return () => clearTimeout(t);
  }, [open]);

  const items: Item[] = useMemo(() => {
    if (query.trim()) {
      return hydrateSearchResults(rankedResults, byId)
        .slice(0, MAX_ITEMS)
        .map((h) => ({ phrase: h.phrase, snippet: h.snippet, terms: h.terms }));
    }
    return recentIds
      .map((id) => byId.get(id))
      .filter((p): p is Phrase => Boolean(p))
      .slice(0, MAX_ITEMS)
      .map((p) => ({ phrase: p, terms: [] }));
  }, [query, rankedResults, byId, recentIds]);

  useEffect(() => setSelected(0), [query]);

  if (!open) return null;

  const openItem = (id: string) => {
    const suffix = query.trim() ? '?q=' + encodeURIComponent(query.trim()) : '';
    navigate('/phrase/' + id + suffix);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => Math.min(s + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => Math.max(s - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[selected];
      if (it) openItem(it.phrase.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const showingRecents = !query.trim();

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '12vh 16px 16px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 96vw)', maxHeight: '74vh',
          background: 'var(--bg-surface)', borderRadius: 'var(--r-lg)',
          border: '1px solid var(--line-divider)', boxShadow: 'var(--shadow-3)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px',
            height: 54, borderBottom: '1px solid var(--line-divider)',
          }}
        >
          <I.search size={20} style={{ color: 'var(--fg-tertiary)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Search ${phrases.length} notes…`}
            enterKeyHint="go"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            autoCapitalize="off"
            style={{
              flex: 1, border: 0, outline: 0, background: 'transparent',
              fontSize: 16, color: 'var(--fg-primary)',
            }}
          />
          <Kbd>esc</Kbd>
        </div>

        <div style={{ overflowY: 'auto', padding: 6 }}>
          {showingRecents && items.length > 0 && (
            <div
              className="t-caption1"
              style={{ padding: '6px 10px 2px', color: 'var(--fg-quaternary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}
            >
              Recent
            </div>
          )}

          {items.length === 0 ? (
            <div className="t-footnote" style={{ padding: '20px 12px', color: 'var(--fg-tertiary)', textAlign: 'center' }}>
              {showingRecents
                ? 'Start typing to search. Try an abbreviation like “dka” or “afib rvr”.'
                : searching ? 'Searching…' : 'No matches.'}
            </div>
          ) : (
            items.map((it, i) => (
              <button
                key={it.phrase.id}
                onClick={() => openItem(it.phrase.id)}
                onMouseMove={() => setSelected(i)}
                ref={(el) => { if (i === selected) el?.scrollIntoView({ block: 'nearest' }); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                  padding: '9px 10px', borderRadius: 'var(--r-md)', border: 0, cursor: 'pointer',
                  background: i === selected ? 'var(--accent-fill)' : 'transparent',
                }}
              >
                <Tag kind={it.phrase.primaryTag}>{it.phrase.tags[0]}</Tag>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--fg-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {it.phrase.name}
                  </span>
                  {it.snippet && (
                    <span className="t-caption1" style={{ display: 'block', color: 'var(--fg-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <span style={{ color: 'var(--fg-quaternary)', fontWeight: 600 }}>{it.snippet.sectionTitle}: </span>
                      <Highlight text={it.snippet.text} terms={it.terms} />
                    </span>
                  )}
                </span>
                {i === selected && <I.arrowR size={15} style={{ color: 'var(--accent)', flexShrink: 0 }} />}
              </button>
            ))
          )}
        </div>

        <div
          className="t-caption1"
          style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '8px 14px',
            borderTop: '1px solid var(--line-divider)', color: 'var(--fg-tertiary)',
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Kbd>↑</Kbd><Kbd>↓</Kbd> navigate</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Kbd>↵</Kbd> open</span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
